import { Component, Input, OnInit, inject } from "@angular/core";
import { BaseDataTablePageDirective } from "@app/view/shared/base-pages/base-data-table-page/base-data-table-page.directive";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
  MatDialogClose,
  MatDialogState
} from "@angular/material/dialog";
import { SystemSearchMetaRestService } from "@app/services/hal/system-search-meta-rest.service";
import { Store } from "@ngrx/store";
import {
  FormControl,
  ReactiveFormsModule,
  UntypedFormBuilder
} from "@angular/forms";
import { Router } from "@angular/router";
import { ServiceErrorHandlerService } from "@app/view/shared/service-error-handler.service";
import { NgxUiLoaderService } from "ngx-ui-loader";
import { ResourceChooserInputData } from "@app/view/shared/resource-chooser-dialog/resource-chooser-dialog.component";
import { VisitTypeStore } from "@app/services/store/visit-type-store";
import { VisitType } from "@app/models/rest/visit-type";
import { MatToolbar, MatToolbarRow } from "@angular/material/toolbar";
import { MatIconAnchor, MatIconButton } from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import {
  MatFormField,
  MatLabel,
  MatSuffix
} from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { GridComponent } from "../base-pages/base-data-table-page/grid.component";
import { MatPaginator } from "@angular/material/paginator";
import { TranslatePipe } from "@ngx-translate/core";
import { CollectionFilter } from "@bryntum/core-thin";
import { GridRowModel } from "@bryntum/grid-thin";
import { debounceTime, distinctUntilChanged, map } from "rxjs/operators";

@Component({
  selector: "app-visit-type-chooser-dialog",
  templateUrl: "./visit-type-chooser-dialog.component.html",
  styleUrls: ["./visit-type-chooser-dialog.component.css"],
  imports: [
    MatToolbar,
    MatToolbarRow,
    MatIconAnchor,
    MatIconButton,
    MatDialogClose,
    MatIcon,
    MatFormField,
    MatLabel,
    MatSuffix,
    MatInput,
    ReactiveFormsModule,
    GridComponent,
    MatPaginator,
    TranslatePipe
  ]
})
export class VisitTypeChooserDialogComponent
  extends BaseDataTablePageDirective<VisitType>
  implements OnInit
{
  dialogRef =
    inject<MatDialogRef<VisitTypeChooserDialogComponent>>(MatDialogRef);
  visitTypeStore: VisitTypeStore;
  protected store: Store<any>;
  protected dialog: MatDialog;
  protected formBuilder: UntypedFormBuilder;
  protected router = inject(Router);
  errorHandler: ServiceErrorHandlerService;
  protected override ngxUiLoaderService: NgxUiLoaderService;
  data = inject<ResourceChooserInputData>(MAT_DIALOG_DATA);

  searchControl = new FormControl("");

  @Input()
  public columns: object[] = [
    {
      field: "description",
      text: "Description",
      flex: 1,
      editor: false
    },
    {
      type: "action",
      text: "",
      width: 50,
      actions: [
        {
          cls: "fas fa-check",
          tooltip: "Select visit type",
          onClick: ({ record }) => {
            this.selectVisitType(record);
          }
        }
      ]
    }
  ];

  // Selecting a visit type closes the dialog with the chosen record. Wired to
  // both a row click (the gesture users expect) and the trailing check icon, so
  // highlighting a row is no longer a dead end (#1510). Guarded against a
  // double-close when both the cell click and action handler fire.
  // `record` is the Bryntum grid row model (modelClass: GridRowModel) — its
  // field values are exposed as properties, and the raw payload is on `.data`.
  onCellClick = (event: { record?: GridRowModel }): void => {
    this.selectVisitType(event?.record);
  };

  private selectVisitType(record?: GridRowModel): void {
    if (record && this.dialogRef.getState() === MatDialogState.OPEN) {
      this.dialogRef.close(record);
    }
  }

  constructor() {
    const systemSearchMetaRestService = inject(SystemSearchMetaRestService);
    const visitTypeStore = inject(VisitTypeStore);
    const store = inject<Store<any>>(Store);
    const dialog = inject(MatDialog);
    const formBuilder = inject(UntypedFormBuilder);
    const errorHandler = inject(ServiceErrorHandlerService);
    const ngxUiLoaderService = inject(NgxUiLoaderService);

    super(
      systemSearchMetaRestService,
      visitTypeStore,
      store,
      dialog,
      "VisitType",
      formBuilder,
      errorHandler,
      ngxUiLoaderService
    );

    this.visitTypeStore = visitTypeStore;
    this.store = store;
    this.dialog = dialog;
    this.formBuilder = formBuilder;
    this.errorHandler = errorHandler;
    this.ngxUiLoaderService = ngxUiLoaderService;
  }

  public get pristine() {
    return (
      !this.gridStore.changes ||
      (this.gridStore.changes.added.length === 0 &&
        this.gridStore.changes.removed.length === 0 &&
        this.gridStore.changes.modified.length === 0)
    );
  }

  override ngOnInit() {
    super.ngOnInit();
    this.gridStore.autoCommit = false;
    this.searchControl.valueChanges
      .pipe(
        map((term) => term?.trim() ?? ""),
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe((term) => this.applySearch(term));
    this.updateRequiredFilters();
  }

  updateRequiredFilters() {
    this.gridStore.load({});
  }

  private applySearch(term: string) {
    this.gridStore.filters.clear();
    if (term) {
      this.gridStore.filters.add(
        new CollectionFilter({
          property: "description",
          operator: "*",
          value: term
        })
      );
    }
    this.gridStore.performFilter(true);
  }
}
